import { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiListAllUsers, apiAdminAssignWrestlerToUser } from '../../api/userApi';
import type { WrestlerWithStats, UserListItem } from '../../types';
import colors from '../../styles/colors';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 24px;
  width: 480px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 1000;
  color: white;
`;

const ModalTitle = styled.h5`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: white;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const UserList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 300px;
`;

const UserRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ $selected }) => $selected ? 'rgba(74, 158, 255, 0.6)' : 'rgba(255,255,255,0.06)'};
  background-color: ${({ $selected }) => $selected ? 'rgba(74, 158, 255, 0.12)' : 'rgba(255,255,255,0.03)'};
  transition: background-color 0.15s, border-color 0.15s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const Username = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const UserMeta = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin-left: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Btn = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.cancel {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }

  &.confirm {
    background-color: #198754;
    color: white;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #f87171;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 16px 0;
`;

interface Props {
  wrestlerData: WrestlerWithStats;
  onClose: (reloadData?: boolean) => void;
}

const ModalAdminAssignWrestlerToUser: FC<Props> = ({ wrestlerData, onClose }) => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiListAllUsers()
      .then(setUsers)
      .catch(() => setError('Errore nel caricamento utenti.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter((u) =>
    u.username.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirm = async (): Promise<void> => {
    if (!selectedUserId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiAdminAssignWrestlerToUser(wrestlerData.id, selectedUserId) as any;

      // 422 — errore applicativo esplicito
      if (res?.success === false) {
        setError(res.message || "Errore nell'assegnazione.");
        setSaving(false);
        return;
      }

      // 401 — non autorizzato
      if (res?.errors?.accessError) {
        setError('Non autorizzato.');
        setSaving(false);
        return;
      }

      // 404 — wrestler o utente non trovato
      if (res?.message && !res.message.toLowerCase().includes('success')) {
        // euristico: se il messaggio non è quello di successo, è un errore
        const isKnownError = ['non trovato', 'non esiste'].some((s) =>
          res.message.toLowerCase().includes(s)
        );
        if (isKnownError) {
          setError(res.message);
          setSaving(false);
          return;
        }
      }

      onClose(true);
    } catch {
      setError("Errore nell'assegnazione. Riprovare.");
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={() => onClose(false)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Assegna {wrestlerData.name} a un utente</ModalTitle>

        <SearchInput
          placeholder="Cerca utente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <UserList>
          {loading && <LoadingText>Caricamento utenti...</LoadingText>}
          {!loading && filtered.length === 0 && (
            <LoadingText>Nessun utente trovato.</LoadingText>
          )}
          {!loading && filtered.map((u) => (
            <UserRow
              key={u.id}
              $selected={selectedUserId === u.id}
              onClick={() => setSelectedUserId(u.id)}
            >
              <Username>{u.username}</Username>
              {u.wrestlers?.length > 0 && (
                <UserMeta>{u.wrestlers.length} wrestler</UserMeta>
              )}
            </UserRow>
          ))}
        </UserList>

        {error && <ErrorText>{error}</ErrorText>}

        <Footer>
          <Btn className="cancel" onClick={() => onClose(false)}>
            Annulla
          </Btn>
          <Btn
            className="confirm"
            disabled={!selectedUserId || saving}
            onClick={handleConfirm}
          >
            {saving ? 'Salvataggio...' : 'Conferma'}
          </Btn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ModalAdminAssignWrestlerToUser;
